// src/pages/orderAssignment/components/BulkAssignModal.jsx
import React, { useState, useRef } from 'react';
import { X, Users, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import styles from './BulkAssignModal.module.css';

/**
 * BulkAssignModal
 *
 * Simple modal to pick one or more users and assign selected orders to them
 * in round-robin fashion (via bulkAssignCreatedOrders endpoint).
 *
 * Props:
 *   isOpen          {boolean}
 *   onClose         {function}
 *   onConfirm       {function(userIds: number[])} → Promise
 *   selectedCount   {number}   — number of selected orders
 *   users           {Array<{id, name, role}>}
 *   isLoading       {boolean}
 */
const BulkAssignModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCount = 0,
  users = [],
  isLoading = false,
}) => {
  const { t } = useLocalization();

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [error, setError] = useState('');
  // Close only on a press that both started and ended on the overlay, so a dropdown
  // collapsing mid-click (shrinking the modal) can't turn into an accidental close.
  const pressStartedOnOverlay = useRef(false);

  if (!isOpen) return null;

  const userOptions = users.map((u) => ({
    key: u.id,
    label: `${u.name}${u.role?.name ? ` (${u.role.name})` : ''}`,
  }));

  const handleConfirm = () => {
    if (selectedUserIds.length === 0) {
      setError(t('orderAssignment.atLeastOneUser'));
      return;
    }
    setError('');
    onConfirm(selectedUserIds);
  };

  const handleClose = () => {
    if (isLoading) return;
    setSelectedUserIds([]);
    setError('');
    onClose();
  };

  return (
    <div
      className={styles.overlay}
      onMouseDown={(e) => { pressStartedOnOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (pressStartedOnOverlay.current && e.target === e.currentTarget) handleClose(); }}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <Users size={20} className={styles.headerIcon} />
            <div>
              <h2>{t('orderAssignment.bulkAssignTitle') || 'Assign Orders'}</h2>
              <p>
                {t('orderAssignment.bulkAssignSubtitle', { count: selectedCount }) ||
                  `Assign ${selectedCount} selected order${selectedCount !== 1 ? 's' : ''} to users`}
              </p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={handleClose} disabled={isLoading}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {error && (
            <div className={styles.errorBanner}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className={styles.infoBox}>
            <p>
              {t('orderAssignment.bulkAssignInfo') ||
                'Selected orders will be distributed in round-robin across the chosen users. Any existing assignments for these orders will be replaced.'}
            </p>
          </div>

          <div className={styles.formGroup}>
            <label>{t('orderAssignment.assignTo')} *</label>
            <MultiSelectFilter
              options={userOptions}
              selectedValues={selectedUserIds}
              onChange={(ids) => {
                setSelectedUserIds(ids);
                if (ids.length > 0) setError('');
              }}
              placeholder={t('orderAssignment.selectUsers') || 'Select users...'}
              searchPlaceholder={t('orderAssignment.searchUsers') || 'Search users...'}
              displayKey="label"
              valueKey="key"
              error={error}
              floatingDropdown
            />
          </div>

          {selectedUserIds.length > 0 && (
            <p className={styles.distributeHint}>
              {t('orderAssignment.distributeHint', {
                orders: selectedCount,
                users: selectedUserIds.length,
              }) ||
                `${selectedCount} order${selectedCount !== 1 ? 's' : ''} will be distributed across ${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''} (round-robin)`}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={handleClose} disabled={isLoading}>
            {t('orderAssignment.cancel') || 'Cancel'}
          </button>
          <button
            className={styles.confirmBtn}
            onClick={handleConfirm}
            disabled={isLoading || selectedUserIds.length === 0}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner} />
                {t('orderAssignment.executing') || 'Assigning...'}
              </>
            ) : (
              <>
                <Users size={16} />
                {t('orderAssignment.executeAssignment') || 'Assign Orders'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;